import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

/**
 * Admin billing configuration: trial settings, plan pricing and promo codes.
 *
 * Kept separate from api/billing.ts, which covers subscriptions and force
 * payment, so the two concerns can be invalidated independently.
 */

export const billingAdminKeys = {
  settings: () => ["billing", "admin", "settings"] as const,
  providers: () => ["billing", "admin", "providers"] as const,
  promoCodes: (filters?: Record<string, unknown>) =>
    ["billing", "admin", "promo-codes", filters ?? {}] as const,
  promoStats: (id: number) => ["billing", "admin", "promo-stats", id] as const,
  priceHistory: (planId: number) =>
    ["billing", "admin", "price-history", planId] as const,
};

// ── Payment provider ─────────────────────────────────────────

export type TProviderKey = "stripe" | "revolut";

/**
 * What a provider can actually do.
 *
 * Stripe and Revolut are not feature-equivalent, and the API reports the
 * difference rather than letting the UI assume parity — a promo field shown
 * while Revolut is active would accept codes that silently do nothing.
 */
export interface TProviderCapabilities {
  promo_codes: boolean;
  billing_portal: boolean;
  hosted_invoices: boolean;
  quantity_updates: boolean;
}

export interface TPaymentProvider {
  key: TProviderKey;
  is_active: boolean;
  /** False when the API keys or webhook secret are missing. */
  configured: boolean;
  capabilities: TProviderCapabilities;
  /**
   * Live subscriptions this provider still bills. Switching away does NOT
   * migrate or cancel them — they keep running on their original provider —
   * so this is shown as context, not as a warning about breakage.
   */
  live_subscriptions: number;
}

export const PROVIDER_LABELS: Record<TProviderKey, string> = {
  stripe: "Stripe",
  revolut: "Revolut",
};

export const useGetPaymentProviders = (enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: billingAdminKeys.providers(),
    enabled,
    queryFn: async () => {
      const { data } = await axios.get(`/admin/billing/providers`);
      return {
        active: (data?.data?.active ?? "stripe") as TProviderKey,
        providers: (data?.data?.providers ?? []) as TPaymentProvider[],
      };
    },
  });

  return {
    activeProvider: data?.active ?? "stripe",
    providers: data?.providers ?? [],
    ...res,
  };
};

export const useUpdatePaymentProvider = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (provider: TProviderKey) => {
      const { data } = await axios.put(`/admin/billing/providers`, { provider });
      return {
        active: (data?.data?.active ?? provider) as TProviderKey,
        providers: (data?.data?.providers ?? []) as TPaymentProvider[],
      };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(billingAdminKeys.providers(), data);
      // The public billing config carries the provider's capabilities, and the
      // checkout UI gates the promo field and portal button on them. Leaving it
      // cached would keep showing controls the new provider cannot serve.
      queryClient.invalidateQueries({ queryKey: ["billing-config"] });
      toast.success(
        t("billing.providerSwitched", "Prestataire de paiement mis à jour")
      );
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      // The API refuses to switch onto a provider with no credentials, and its
      // message names what is missing — worth surfacing verbatim rather than
      // replacing with a generic failure.
      toast.error(
        err?.response?.data?.message ??
          t("billing.providerSwitchError", "Échec du changement de prestataire")
      );
    },
  });
};

// ── Settings ──────────────────────────────────────────────────────────

export type TSettingType = "string" | "int" | "bool" | "json";

export interface TAppSetting {
  key: string;
  value: string | number | boolean | null;
  default: string | number | boolean | null;
  type: TSettingType;
  group: string;
  label: string;
  description: string;
}

/** The three keys the billing settings screen writes. */
export interface TBillingSettingsPayload {
  trial_enabled?: boolean;
  trial_days?: number;
  promo_enabled?: boolean;
}

export const SETTING_KEYS = {
  trialEnabled: "billing.trial.enabled",
  trialDays: "billing.trial.days",
  promoEnabled: "billing.promo.enabled",
} as const;

/**
 * Turn the API's flat list into the shape the form binds to. Reading by key
 * rather than by array position means adding a setting server-side cannot
 * silently shift what the form is editing.
 */
export const settingsToForm = (settings: TAppSetting[]) => {
  const byKey = new Map(settings.map((s) => [s.key, s.value]));
  return {
    trial_enabled: Boolean(byKey.get(SETTING_KEYS.trialEnabled)),
    trial_days: Number(byKey.get(SETTING_KEYS.trialDays) ?? 14),
    promo_enabled: Boolean(byKey.get(SETTING_KEYS.promoEnabled)),
  };
};

export const useGetBillingSettings = (enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: billingAdminKeys.settings(),
    enabled,
    queryFn: async (): Promise<TAppSetting[]> => {
      const { data } = await axios.get(`/admin/billing/settings`);
      return data?.data ?? [];
    },
  });
  return { settings: data ?? [], ...res };
};

export const useUpdateBillingSettings = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TBillingSettingsPayload) => {
      const { data } = await axios.put(`/admin/billing/settings`, payload);
      return (data?.data ?? []) as TAppSetting[];
    },
    onSuccess: (data) => {
      // Seed the cache with the response rather than refetching: the API
      // returns the full effective settings list already.
      queryClient.setQueryData(billingAdminKeys.settings(), data);
      toast.success(t("billing.settingsSaved", "Paramètres enregistrés"));
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.settingsError", "Échec de l'enregistrement")
      );
    },
  });
};

// ── Pricing ───────────────────────────────────────────────────────────

export interface TPriceChangePayload {
  planId: number;
  billing_cycle: "monthly" | "annual";
  /** New unit amount in cents. */
  price_cents: number;
  reason?: string;
}

export interface TPriceHistoryRow {
  id: number;
  plan_id: number;
  billing_cycle: "monthly" | "annual";
  old_price_cents: number | null;
  new_price_cents: number;
  old_stripe_price_id: string | null;
  new_stripe_price_id: string | null;
  currency: string;
  reason: string | null;
  created_at: string;
}

/**
 * Change a plan's price.
 *
 * The API creates a new Stripe Price and archives the old one, because Stripe
 * Prices are immutable. Existing subscribers keep their current price — the
 * response reports how many, so the UI can say so.
 */
export const useUpdatePlanPrice = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId, ...payload }: TPriceChangePayload) => {
      const { data } = await axios.put(
        `/admin/billing/plans/${planId}/price`,
        payload
      );
      return data?.data as {
        plan: any;
        grandfathered_subscriptions: number;
      };
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["billing", "plans"] });
      queryClient.invalidateQueries({
        queryKey: billingAdminKeys.priceHistory(variables.planId),
      });

      const affected = data?.grandfathered_subscriptions ?? 0;
      toast.success(
        affected > 0
          ? t(
              "billing.priceUpdatedGrandfathered",
              "Tarif mis à jour. {{count}} abonnement(s) en cours conservent l'ancien tarif.",
              { count: affected }
            )
          : t("billing.priceUpdated", "Tarif mis à jour")
      );
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.priceUpdateError", "Échec de la mise à jour du tarif")
      );
    },
  });
};

export const useGetPlanPriceHistory = (planId: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: billingAdminKeys.priceHistory(planId),
    enabled: enabled && planId > 0,
    queryFn: async (): Promise<TPriceHistoryRow[]> => {
      const { data } = await axios.get(
        `/admin/billing/plans/${planId}/price-history`
      );
      return data?.data ?? [];
    },
  });
  return { history: data ?? [], ...res };
};

// ── Promo codes ───────────────────────────────────────────────────────

export type TDiscountType = "percent" | "amount";
export type TPromoDuration = "once" | "repeating" | "forever";

export interface TPromoCode {
  id: number;
  code: string;
  name: string | null;
  discount_type: TDiscountType;
  percent_off: number | null;
  amount_off_cents: number | null;
  currency: string;
  duration: TPromoDuration;
  duration_in_months: number | null;
  max_redemptions: number | null;
  times_redeemed: number;
  applies_to_plan_ids: number[] | null;
  first_time_only: boolean;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface TPromoCodePayload {
  code: string;
  name?: string;
  discount_type: TDiscountType;
  percent_off?: number;
  amount_off_cents?: number;
  currency?: string;
  duration?: TPromoDuration;
  duration_in_months?: number;
  max_redemptions?: number | null;
  applies_to_plan_ids?: number[] | null;
  first_time_only?: boolean;
  starts_at?: string | null;
  expires_at?: string | null;
}

export interface TPromoCodeFilters {
  // Index signature so the object can be passed straight to the query-key
  // builder, which takes a Record<string, unknown>.
  [key: string]: unknown;
  search?: string;
  is_active?: boolean;
  status?: "redeemable" | "expired";
  page?: number;
  per_page?: number;
}

export interface TPromoStats {
  promo_code: TPromoCode;
  redemption_count: number;
  remaining_redemptions: number | null;
  total_discount_cents: number;
  currency: string;
  is_redeemable: boolean;
  unusable_reason: string | null;
}

export const useGetPromoCodes = (filters: TPromoCodeFilters = {}) => {
  const { data, ...res } = useQuery({
    queryKey: billingAdminKeys.promoCodes(filters),
    queryFn: async () => {
      const { data } = await axios.get(`/admin/billing/promo-codes`, {
        params: filters,
      });
      // Lumen's paginator response: { data: [...], total, current_page, ... }
      return {
        items: (data?.data ?? []) as TPromoCode[],
        total: Number(data?.total ?? 0),
        page: Number(data?.current_page ?? 1),
        perPage: Number(data?.per_page ?? 25),
      };
    },
  });

  return {
    promoCodes: data?.items ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? 1,
    perPage: data?.perPage ?? 25,
    ...res,
  };
};

export const useCreatePromoCode = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TPromoCodePayload) => {
      const { data } = await axios.post(`/admin/billing/promo-codes`, payload);
      return data?.data as TPromoCode;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["billing", "admin", "promo-codes"],
      });
      toast.success(t("billing.promoCreated", "Code promo créé"));
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.promoCreateError", "Échec de la création du code promo")
      );
    },
  });
};

/**
 * Only the mutable fields. The API rejects changes to the discount amount,
 * type or duration because Stripe coupons are immutable — the form disables
 * those inputs in edit mode for the same reason.
 */
export const useUpdatePromoCode = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: {
      id: number;
      name?: string;
      applies_to_plan_ids?: number[] | null;
      starts_at?: string | null;
      expires_at?: string | null;
      is_active?: boolean;
    }) => {
      const { data } = await axios.put(
        `/admin/billing/promo-codes/${id}`,
        payload
      );
      return data?.data as TPromoCode;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["billing", "admin", "promo-codes"],
      });
      toast.success(t("billing.promoUpdated", "Code promo mis à jour"));
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.promoUpdateError", "Échec de la mise à jour")
      );
    },
  });
};

export const useTogglePromoCode = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await axios.post(
        `/admin/billing/promo-codes/${id}/toggle`
      );
      return data?.data as TPromoCode;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["billing", "admin", "promo-codes"],
      });
      toast.success(
        data?.is_active
          ? t("billing.promoEnabled", "Code promo activé")
          : t("billing.promoDisabled", "Code promo désactivé")
      );
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.promoToggleError", "Échec du changement de statut")
      );
    },
  });
};

export const useArchivePromoCode = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`/admin/billing/promo-codes/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["billing", "admin", "promo-codes"],
      });
      toast.success(t("billing.promoArchived", "Code promo archivé"));
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.promoArchiveError", "Échec de l'archivage")
      );
    },
  });
};

export const useGetPromoCodeStats = (id: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: billingAdminKeys.promoStats(id),
    enabled: enabled && id > 0,
    queryFn: async (): Promise<TPromoStats | null> => {
      const { data } = await axios.get(
        `/admin/billing/promo-codes/${id}/stats`
      );
      return data?.data ?? null;
    },
  });
  return { stats: data ?? null, ...res };
};

// ── Formatting helpers ────────────────────────────────────────────────

export const formatCents = (cents: number | null | undefined, currency = "eur") =>
  `${((cents ?? 0) / 100).toFixed(2)} ${currency.toUpperCase()}`;

export const discountLabel = (promo: TPromoCode) =>
  promo.discount_type === "percent"
    ? `${promo.percent_off}%`
    : formatCents(promo.amount_off_cents, promo.currency);
